import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../hooks/useStore';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

export const Button = observer<ButtonProps>(({
  title,
  variant = 'primary',
  size = 'medium',
  loading = false,
  fullWidth = true,
  icon,
  disabled,
  className = '',
  ...props
}) => {
  const { themeStore } = useStore();
  const { themeClasses } = themeStore;

  const isDisabled = disabled || loading;

  const sizeClasses = {
    small: 'py-2 px-4',
    medium: 'py-3 px-5',
    large: 'py-4 px-6'
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  const getVariantClasses = () => {
    if (isDisabled) {
      return themeClasses.button.disabled;
    }

    switch (variant) {
      case 'primary':
        return themeClasses.button.primary;
      case 'secondary':
        return themeClasses.button.secondary;
      case 'danger':
        return 'bg-red-600 shadow-lg shadow-red-600/25 hover:bg-red-700';
      default:
        return themeClasses.button.primary;
    }
  };

  const getTextColor = () => {
    if (isDisabled) {
      return themeStore.isDarkMode ? 'text-zinc-500' : 'text-gray-400';
    }

    if (variant === 'secondary') {
      return themeClasses.text.primary;
    }

    return 'text-white';
  };

  return (
    <button
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center ${
        fullWidth ? 'w-full' : ''
      } ${getVariantClasses()} ${className} transition-all duration-200 disabled:cursor-not-allowed`}
      disabled={isDisabled}
      style={{
        opacity: isDisabled ? 0.6 : 1
      }}
      {...props}
    >
      {loading ? (
        <div className="flex flex-row items-center justify-center">
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className={`${textSizeClasses[size]} font-bold ml-2 ${getTextColor()}`}>
            {title}
          </span>
        </div>
      ) : (
        <div className="flex flex-row items-center justify-center">
          {icon && (
            <div className="mr-2">
              {icon}
            </div>
          )}
          <span className={`${textSizeClasses[size]} font-bold tracking-wide ${getTextColor()}`}>
            {title}
          </span>
        </div>
      )}
    </button>
  );
});
